// «Varer fra butikken» på en service (WorkshopDetails): varer solgt i butikken (ski, binding,
// støvler …) som hører til servicen. Varene vises samlet på servicefakturaen, men er ikke del av
// servicens beløp, Vipps-betalingen eller verkstedstatistikken (src/lib/butikkvarer.js).
// To veier inn:
//   - «Legg til varer»: modal med søk i /produkter (VareLinjer, som pristilbudet), lagres rett på
//     servicen (WorkshopService.butikkvarelinjer). Brukes når servicen kom først.
//   - Koble en ordre/et skiplukk eller et pristilbud som allerede har varene (skiplukk → service).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { knappPrimar, knappSekundar } from '../ny-kundesamhandling/Skjema';
import Ikon from '../ny-kundesamhandling/Ikon';
import { useDialog } from '../components/tilgjengelighet';
import VareLinjer from './VareLinjer';
import { oppsummering, kr } from './kalkyle';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TYPE = { ordre: 'Ordre', skiplukk: 'Skiplukk', tilbud: 'Pristilbud' };
const TOM = [];
const sumAv = (linjer) => linjer.reduce((s, l) => s + (Number(l.totalprisEtterRabatt) || 0), 0);

function VareListe({ linjer, sumTekst }) {
  return (
    <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white text-sm">
      {linjer.map((l, i) => (
        <li key={i} className="flex flex-wrap justify-between gap-x-3 px-3 py-1.5">
          <span className="min-w-0 [overflow-wrap:anywhere]">{l.artikkel}{Number(l.antall) > 1 ? ` · ${l.antall} stk` : ''}</span>
          <span className="font-semibold">{kr(Number(l.totalprisEtterRabatt) || 0)}</span>
        </li>
      ))}
      <li className="flex justify-between gap-3 px-3 py-1.5 font-semibold">
        <span>{sumTekst}</span><span>{kr(sumAv(linjer))}</span>
      </li>
    </ul>
  );
}

// Modal for varene på servicen, med samme varelinjer og produktsøk som pristilbudet.
function VareModal({ apen, startLinjer, onLukk, onLagre }) {
  const [linjer, setLinjer] = useState([]);
  const [lagrer, setLagrer] = useState(false);
  const [feil, setFeil] = useState('');
  const dialogRef = useDialog(apen, () => { if (!lagrer) onLukk(); });

  useEffect(() => {
    if (!apen) return;
    setLinjer(startLinjer.map((l) => ({ ...l, rabattKr: l.rabattKr ?? l.totalRabattKr ?? 0 })));
    setFeil('');
  }, [apen, startLinjer]);

  if (!apen) return null;
  const sum = oppsummering(linjer);

  const lagre = async () => {
    if (linjer.some((l) => !(Number(String(l.pris ?? '').replace(',', '.')) > 0))) {
      setFeil('Sett pris på alle varene.');
      return;
    }
    setLagrer(true);
    setFeil('');
    try {
      await onLagre(linjer);
    } catch (e) {
      setFeil(e.message);
    } finally {
      setLagrer(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-black/60 p-2 sm:p-5">
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="sbv-tittel" aria-describedby="sbv-forklaring"
        className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-3 py-2 sm:px-4">
          <h2 id="sbv-tittel" className="text-base font-bold text-slate-900">Varer fra butikken</h2>
          <p id="sbv-forklaring" className="text-sm text-slate-700">
            Varer kunden kjøper i butikken sammen med servicen. De vises på servicefakturaen og betales i kassa, men er
            ikke med i servicens beløp eller i verkstedstatistikken. Deler og arbeid til servicen legges inn som vanlig.
          </p>
        </div>
        <div className="space-y-3 px-3 py-3 sm:px-4">
          <VareLinjer idPrefiks="sbv" linjer={linjer} onEndre={setLinjer} visVerksted={false} />
          {linjer.length > 0 && (
            <p className="flex items-baseline justify-between gap-2 rounded-xl border border-slate-200 px-3 py-1.5">
              <span className="font-semibold text-slate-900">Varer fra butikken{sum.rabattKr > 0 ? ` (rabatt ${kr(sum.rabattKr)})` : ''}</span>
              <span className="text-lg font-bold text-slate-900">{kr(sum.total)}</span>
            </p>
          )}
          {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={lagre} disabled={lagrer} className={knappPrimar}>
              <Ikon navn="hake" className="w-6 h-6" /> {lagrer ? 'Lagrer …' : 'Lagre varene'}
            </button>
            <button type="button" onClick={onLukk} disabled={lagrer} className={knappSekundar}>Avbryt</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ServiceButikkvarer({ serviceId, onKobling }) {
  const [data, setData] = useState(null);
  const [valgt, setValgt] = useState('');
  const [arbeider, setArbeider] = useState(false);
  const [modalApen, setModalApen] = useState(false);
  const [feil, setFeil] = useState('');

  const hent = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${serviceId}/butikkvarer`);
      if (!r.ok) throw new Error();
      const b = await r.json();
      setData(b);
      // Til servicefakturaen: varene fra en koblet ordre/et tilbud og varene lagt rett på servicen samlet.
      const egne = Array.isArray(b.linjer) ? b.linjer : [];
      const k = b.kobling;
      onKobling?.(k || egne.length
        ? { ...(k || {}), linjer: [...(k?.linjer || []), ...egne], sum: (k?.sum || 0) + sumAv(egne), egneLinjer: egne.length }
        : null);
    } catch {
      setFeil('Klarte ikke å hente varene fra butikken.');
    }
  }, [serviceId, onKobling]);

  useEffect(() => { if (serviceId) hent(); }, [serviceId, hent]);

  const endre = async (metode, body) => {
    setArbeider(true);
    setFeil('');
    try {
      const r = await fetch(`${API_BASE_URL}/workshop-services/${serviceId}/butikkvarer`, {
        method: metode,
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.message || 'Klarte ikke å lagre koblingen.');
      setValgt('');
      await hent();
    } catch (e) {
      setFeil(e.message);
    } finally {
      setArbeider(false);
    }
  };

  const lagreLinjer = async (linjer) => {
    const r = await fetch(`${API_BASE_URL}/workshop-services/${serviceId}/butikkvarer/linjer`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ linjer }),
    });
    const b = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(b.message || 'Klarte ikke å lagre varene.');
    setModalApen(false);
    await hent();
  };

  const koble = () => {
    const k = (data?.kandidater || []).find((x) => `${x.type}:${x._id}` === valgt);
    if (k) endre('PUT', k.type === 'tilbud' ? { tilbudId: k._id } : { ordreId: k._id });
  };

  if (!data && !feil) return null;
  const aktiv = data?.aktiv !== false;
  const egne = data?.linjer || TOM;
  const kobling = data?.kobling;
  const kandidater = data?.kandidater || [];

  return (
    <section aria-labelledby="sb-tittel" className="mb-4 space-y-2 rounded-xl border border-sky-200 bg-sky-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="sb-tittel" className="font-semibold text-slate-900">Varer fra butikken</h3>
        {aktiv && data && (
          <button type="button" onClick={() => setModalApen(true)} className={knappSekundar}>
            <Ikon navn={egne.length ? 'blyant' : 'pluss'} className="w-5 h-5" /> {egne.length ? 'Endre varer' : 'Legg til varer'}
          </button>
        )}
      </div>

      {egne.length > 0 && <VareListe linjer={egne} sumTekst={kobling ? 'Varer lagt på servicen' : 'Varer fra butikken'} />}

      {kobling && (
        <>
          <p className="text-sm text-slate-800">
            {kobling.ordre && (
              <>
                {TYPE[kobling.ordre.type] || 'Ordre'}{' '}
                <Link to={`/order-details/${kobling.ordre._id}`} className="font-semibold text-indigo-700 underline underline-offset-2">
                  #{kobling.ordre.ordreid}
                </Link>
                {kobling.ordre.tekst ? ` · ${kobling.ordre.tekst}` : ''}
              </>
            )}
            {kobling.tilbud && (
              <>
                {kobling.ordre ? ' · ' : ''}Pristilbud{' '}
                <Link to={`/offer-details/${kobling.tilbud._id}`} className="font-semibold text-indigo-700 underline underline-offset-2">
                  #{kobling.tilbud.offerRef}
                </Link>
                {kobling.tilbud.status === 'godkjent' ? ' (godkjent)' : ` (${kobling.tilbud.status || 'utkast'})`}
              </>
            )}
          </p>
          {kobling.linjer.length ? (
            <VareListe linjer={kobling.linjer} sumTekst={egne.length ? 'Varer fra ordren' : 'Varer fra butikken'} />
          ) : (
            <p className="text-sm font-medium text-amber-900">
              Ingen priser på ordren ennå. Sett pris eller lag et pristilbud på ordren, så kommer varene med på servicefakturaen.
            </p>
          )}
          {kobling.verkstedLinjerUtelatt > 0 && (
            <p className="text-sm text-slate-700">
              {kobling.verkstedLinjerUtelatt === 1 ? '1 linje med verkstedarbeid' : `${kobling.verkstedLinjerUtelatt} linjer med verkstedarbeid`} fra
              tilbudet er ikke med her. Verkstedarbeidet føres på servicen.
            </p>
          )}
          <button type="button" onClick={() => endre('DELETE')} disabled={arbeider} className="text-sm font-semibold text-red-800 underline underline-offset-2 disabled:opacity-50">
            Fjern koblingen til ordren
          </button>
        </>
      )}

      {!aktiv ? (
        <p className="text-sm text-slate-700">Servicen er avsluttet eller kansellert, så varene kan ikke endres.</p>
      ) : egne.length || kobling ? (
        <p className="text-sm text-slate-700">
          Varene vises på servicefakturaen og betales i kassa. De er ikke med i servicens beløp eller i verkstedstatistikken.
        </p>
      ) : (
        <p className="text-sm text-slate-800">
          Har kunden kjøpt varer i butikken til denne servicen, f.eks. ski og binding? Legg dem til her, så vises de samlet på
          servicefakturaen. Verkstedstatistikken teller bare servicens egne linjer og deler.
        </p>
      )}

      {aktiv && !kobling && kandidater.length > 0 && (
        <details className="rounded-lg">
          <summary className="cursor-pointer text-sm font-semibold text-indigo-800">Varene står allerede på en ordre, et skiplukk eller et pristilbud</summary>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 basis-64">
              <label htmlFor="sb-valg" className="mb-1 block text-sm font-medium text-slate-700">Ordre eller pristilbud</label>
              <select id="sb-valg" value={valgt} onChange={(e) => setValgt(e.target.value)}
                className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-1.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30">
                <option value="">Velg …</option>
                {kandidater.map((k) => (
                  <option key={`${k.type}:${k._id}`} value={`${k.type}:${k._id}`}>
                    {TYPE[k.type]} #{k.nr}{k.tekst ? ` – ${k.tekst}` : ''}{k.status ? ` (${k.status})` : ''}{k.harPris ? '' : ' – uten pris'}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" onClick={koble} disabled={!valgt || arbeider} className={knappSekundar}>Koble til servicen</button>
          </div>
        </details>
      )}

      {feil && <p role="alert" className="text-sm font-medium text-red-800">{feil}</p>}

      <VareModal apen={modalApen} startLinjer={egne} onLukk={() => setModalApen(false)} onLagre={lagreLinjer} />
    </section>
  );
}
